import { useGetSalesPersonByBusinessPartnerId } from '@/api/business-partner/business-partner.hooks';
import { useDownloadStockList } from '@/api/stock-lists/stock-lists.hooks';
import { useGlobalStore } from '@/stores';
import { useSnackbar } from 'notistack';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  Download,
  ExpandMore,
  LocalShipping,
  Storage,
  TableChart,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '10px !important',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
  boxShadow: 'none',
  overflow: 'hidden',
};

const summarySx = {
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    gap: 1.5,
  },
};

export function ResourcesSection() {
  const { t } = useTranslation('customer');
  const { data: salesPerson } = useGetSalesPersonByBusinessPartnerId();
  const salesPersonEmail =
    salesPerson?.email || 'mobile@elink-distribution.com';

  const businessPartnerId = useGlobalStore((s) => s.businessPartnerId);
  const { enqueueSnackbar } = useSnackbar();
  const downloadStockList = useDownloadStockList({
    onError: () =>
      enqueueSnackbar('Download der Bestandsliste fehlgeschlagen.', {
        variant: 'error',
      }),
  });

  const handleDownload = (format: 'xlsx' | 'csv') => {
    if (!businessPartnerId) return;
    downloadStockList.mutate({ businessPartnerId, format });
  };

  const emailBody = t('welcome.ftpEmailBody').replace(/\n/g, '%0D%0A');
  const emailSubject = t('welcome.ftpEmailSubject');
  const emailLink = `mailto:${salesPersonEmail}?subject=${emailSubject}&body=${emailBody}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} id="resources-section">
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'text.secondary',
          fontSize: '0.75rem',
        }}
      >
        Services & Downloads
      </Typography>

      {/* FTP Anbindung */}
      <Accordion disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={summarySx}>
          <Storage sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            FTP Anbindung
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Erhalten Sie Zugang zu unserem FTP-Server für den automatisierten
            Datenaustausch von Preislisten und Bestandsdaten.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Storage />}
            href={emailLink}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            FTP-Zugang anfragen
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Preislisten Download */}
      <Accordion disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={summarySx}>
          <TableChart sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Preislisten Download
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aktuelle Preisliste mit Lagerbeständen herunterladen.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={() => handleDownload('xlsx')}
              disabled={!businessPartnerId || downloadStockList.isPending}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Download .xlsx
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={() => handleDownload('csv')}
              disabled={!businessPartnerId || downloadStockList.isPending}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Download .csv
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Versand im Namen Dritter */}
      <Accordion disableGutters sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={summarySx}>
          <LocalShipping sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Versand im Namen Dritter
          </Typography>
          <Chip label="NEU" variant="filled" color="secondary" size="small" />
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            In Zukunft haben Sie die Möglichkeit, Ihr Logo als .png-Datei
            direkt bei uns hochzuladen und individuelle Einstellungen für den
            Versand mit Ihrem Lieferschein vorzunehmen. So können Sie ganz
            einfach Ihr Logo und Ihre Adresse auf unseren Lieferschein drucken
            lassen, damit Ihre Kunden den Versand als Ihre eigene Leistung
            wahrnehmen. Dieser Premium-Service unterstützt Sie dabei, Ihr
            Geschäft weiter zu stärken und bietet eine ideale Lösung für Ihren
            professionellen Auftritt! Bitte beachten Sie, dass dieser Service
            kostenpflichtig ist. Sprechen Sie uns bei Interesse gerne darauf an!
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowForward />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Jetzt einrichten
          </Button>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
